import React from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { Redirect } from 'react-router-dom';
import { ACCOUNT_SETTINGS, PERMISSIONS, ROUTES } from 'src/constants';
import {
  refreshCustomFields,
  setCurrentNavigationTab,
  setVisibleModalFormNumber,
} from 'src/actions/accountSettingsActions';
import helpers from 'src/helpers';
import {
  isTelnyxPrePort as evalIsTelnyxPrePort,
  isTelnyxPortDraftSaved as evalIsTelnyxPortDraftSaved,
  isTelnyxPortInFlight as evalIsTelnyxPortInFlight,
  shouldLockToTCRTab as evalShouldLockToTCRTab,
} from 'src/helpers/telnyxPortPendingGates';
import SetupAssistantDefaultScreen from 'src/components/SetupAssistantDefaultScreen';
import { needOtherInfoNow, needCustomFieldsNow } from './helpers/check';

const { PERMISSIONS_SYSTEM_CODE } = PERMISSIONS;

/**
 * firstPermittedRedirect
 * The destination list as it has always worked: the first entry whose
 * permissions the account holds, as a <Redirect>, or null.
 */
const firstPermittedRedirect = (defaultRedirects, userRolePermissions) => {
  // eslint-disable-next-line no-restricted-syntax
  for (const defaultRedirect of defaultRedirects) {
    if (defaultRedirect?.url && (!defaultRedirect?.permissions || helpers.permissions.doesUserHaveOneOfPermissions(defaultRedirect?.permissions, userRolePermissions))) {
      return <Redirect to={defaultRedirect.url} />;
    }
  }

  return null;
};

function RedirectLogic({ defaultRedirects }) {
  const dispatch = useDispatch();
  const {
    user,
    pbAPIKeyValid,
    stids,
  } = useSelector((state) => state?.account || {});
  const {
    currentNavigationTab,
    visibleModalFormNumber,
  } = useSelector((state) => state?.accountSettings || {});

  const {
    userRole,
    userSubscription,
    userPhoneNumbersInfo,
  } = user || {};

  const {
    permissions: userRolePermissions,
  } = userRole || {};

  // Early return if no user
  if (helpers.isEmpty(user)) {
    return null;
  }

  const doesUserHaveSubscription = helpers.doesUserHaveSubscription(user);
  const isAdmin = helpers.isUserAdmin(user);
  const currentPath = window.location.pathname;

  // Only check redirects if we're not already on the account page
  const shouldCheckRedirects = currentPath.indexOf(ROUTES.APP_ACCOUNT) !== 0;
  const canShowHelpAndInformation = !helpers.isEmptyApiKey(user) && window.location.pathname.indexOf(ROUTES.APP_HELP_AND_INFORMATION) === 0;

  // Required Tab Check
  const currentIsRequiredTab = currentNavigationTab === 'required' && currentPath.indexOf(ROUTES.APP_ACCOUNT) === 0;
  if (shouldCheckRedirects && helpers.isPageBlocked(user) && !currentIsRequiredTab && !canShowHelpAndInformation) {
    dispatch(setCurrentNavigationTab(false));
    return <Redirect to={ROUTES.APP_ACCOUNT} />;
  }

  // Subscription Tab Check
  const currentIsSubscriptionTab = currentNavigationTab === 'subscription' && currentPath.indexOf(ROUTES.APP_ACCOUNT) === 0;
  if (!isAdmin && !helpers.isPageBlocked(user) && !doesUserHaveSubscription && !currentIsSubscriptionTab && !canShowHelpAndInformation) {
    dispatch(setCurrentNavigationTab('subscription'));
    return <Redirect to={ROUTES.APP_ACCOUNT} />;
  }

  // Enhanced Texting Subscription Flow Logic
  const isCurrentSubscriptionForTexting = helpers.isArray(stids) && stids.includes(userSubscription?.currentSubscriptionId);
  const doesUserHaveTextingPhoneNumber = !!userPhoneNumbersInfo?.phoneNumber;
  const userTCRRegistration = user?.userTCRRegistration;

  // Helper function to check if TCR registration needs attention
  // Note: 'rejected' is treated like 'submitted' - user can still use CP while we handle it internally
  // Fix B: 'pending_manual_submit' (Piece 1 hold-gate) and 'pending' (campaign
  // created, awaiting Telnyx verdict) both mean the user has done everything
  // they can do on their end. Lock them onto the TCR tab and they're stuck
  // staring at an in-review screen with no actions — the SQL-flip workaround
  // we did three times for Matty Hyatt (sp 2910). Unlocking nav doesn't hide
  // status; they still see the in-review messaging when they visit the TCR tab.
  // 'carrier_approved' (Telnyx: carriers approved the campaign, awaiting a
  // phone number — port in flight or not yet purchased) is strictly further
  // along than 'pending' and the same rationale applies: nothing actionable
  // for the user, so it releases navigation identically (Stacy Itzel,
  // sp 1910, was route-locked 2026-07-08 when the status shipped without
  // this entry).
  const needsTCRRegistration = (tcr) => {
    if (!tcr) return true;
    return !['submitted', 'rejected', 'ready', 'approved', 'pending_manual_submit', 'pending', 'carrier_approved'].includes(tcr.status);
  };

  // Telnyx-gated pre-completion: user subscribed, has no phone number, no
  // saved port draft yet, and has not submitted a port. They must choose
  // new-number vs port before anything else. The ONLY allowed location
  // during this phase is the Account page (the decision modal, the
  // choose-a-number modal chain, or the Port tab — all live under
  // /app/account) or the Help page.
  //
  // This is a HARD route lock: it does NOT depend on a modal being open.
  // Ordered BEFORE Scenario 1 so a user who chose "port" and is on the
  // Port tab is not yanked into a modal. Lumian/Twilio users skip this
  // entirely and hit the unchanged Scenario 1 below — ZERO behavior
  // change.
  //
  // CON-1488: once the user clicks Continue on the Port tab the backend
  // persists a draft row (status='draft', no Telnyx port API call) and
  // sets hasSavedPortDraft=true on the viewmodel. evalIsTelnyxPrePort
  // (in src/helpers/telnyxPortPendingGates.js) treats that as "no longer
  // hard-locked to Port tab" so the user can complete TCR registration
  // in parallel with the 1-2 day port-out PIN wait.
  const currentIsAccountPage = currentPath.indexOf(ROUTES.APP_ACCOUNT) === 0;
  const currentIsPortTab = currentNavigationTab === 'phoneNumberPort' && currentIsAccountPage;
  const isTelnyxPrePort = !isAdmin
    && !helpers.isPageBlocked(user)
    && doesUserHaveSubscription
    && isCurrentSubscriptionForTexting
    && evalIsTelnyxPrePort(user);

  if (isTelnyxPrePort) {
    // Off the Account page (e.g. a sidebar destination) — HARD redirect
    // back, regardless of any modal flag. Re-assert the decision modal so
    // they land on a usable surface.
    if (!currentIsAccountPage && !canShowHelpAndInformation) {
      if (!visibleModalFormNumber) {
        // Return a port-first user to the Port tab (where they were before
        // the blocked sidebar nav), not the Subscription tab. CON-1488 /
        // Diana QA: previously landed on Subscription/Your Info.
        //
        // Deliberately do NOT open the CHOOSE_NEW_OR_PORT decision modal
        // here: a user reaching this branch has already chosen "port" and
        // is mid-flow; re-prompting the decision as a side effect of a
        // blocked sidebar nav is wrong. A genuine first-time port-first
        // user still gets the decision modal via the on-account branch
        // below (lines ~122-125), which is unchanged.
        dispatch(setCurrentNavigationTab('phoneNumberPort'));
      }
      return <Redirect to={ROUTES.APP_ACCOUNT} />;
    }

    // On the Port tab (they chose "port") — allowed, stay put.
    if (currentIsPortTab || canShowHelpAndInformation) {
      return null;
    }

    // On the Account page but not the Port tab and no modal open — (re)show
    // the decision modal so they cannot sit on some other account tab.
    if (!visibleModalFormNumber) {
      dispatch(setCurrentNavigationTab('subscription'));
      dispatch(setVisibleModalFormNumber(ACCOUNT_SETTINGS.MODAL_NUMBERS.CHOOSE_NEW_OR_PORT));
      return <Redirect to={ROUTES.APP_ACCOUNT} />;
    }

    // On the Account page with the modal/choose-a-number chain open — let
    // it run; do not fall through to Scenario 1.
    return null;
  }

  // Scenario 1: User has texting subscription but no phone number.
  //
  // CON-1488: exempt Telnyx port-draft users. A user with hasSavedPortDraft
  // has already chosen "port" and saved Step 1; they are waiting on a
  // port-out PIN from their carrier. Showing them the "choose a new
  // number" modal here would yank them back into the pre-decision UX
  // they already completed, and undoes the auto-navigation to the TCR
  // tab that Step 1 Save dispatches. Lumian/Twilio users never set
  // hasSavedPortDraft, so this is a pure addition for them.
  //
  // Also exempt in-flight porters (submitted/pending/scheduled — Tina
  // Glotzbach, June 2026): their number decision is equally already made.
  // Previously this branch fired for them and set a phantom
  // CHOOSE_NEW_NUMBER modal state (the modal itself render-suppresses for
  // committed ports per the Kristen Paul fix), which parked them on the
  // Subscription tab and blocked Scenario 2 from routing them to TCR.
  if (!isAdmin
    && !helpers.isPageBlocked(user)
    && doesUserHaveSubscription
    && isCurrentSubscriptionForTexting
    && !doesUserHaveTextingPhoneNumber
    && !evalIsTelnyxPortDraftSaved(user)
    && !evalIsTelnyxPortInFlight(user)
    && !visibleModalFormNumber) {
    dispatch(setCurrentNavigationTab('subscription'));
    dispatch(setVisibleModalFormNumber(ACCOUNT_SETTINGS.MODAL_NUMBERS.CHOOSE_NEW_NUMBER));
    return <Redirect to={ROUTES.APP_ACCOUNT} />;
  }

  // Scenario 2: User has texting subscription and either a phone number OR
  // a saved/in-flight Telnyx port (CON-1488; broadened past draft for Tina
  // Glotzbach, June 2026), and needs TCR registration. The port-row branch
  // lets a Telnyx port-pending user complete TCR while the port is drafted
  // or already with the carrier. Lumian/Twilio users never have
  // hasSavedPortDraft/hasPortInFlight set so this branch is a pure
  // addition — existing behavior is preserved by the same
  // doesUserHaveTextingPhoneNumber signal as before.
  const currentIsTCRTab = currentNavigationTab === 'theCampaignRegistry' && currentPath.indexOf(ROUTES.APP_ACCOUNT) === 0;
  if (!isAdmin
    && !helpers.isPageBlocked(user)
    && doesUserHaveSubscription
    && isCurrentSubscriptionForTexting
    && evalShouldLockToTCRTab(user)
    && needsTCRRegistration(userTCRRegistration)
    && !currentIsTCRTab
    && !canShowHelpAndInformation
    && !visibleModalFormNumber) {
    dispatch(setCurrentNavigationTab('theCampaignRegistry'));
    return <Redirect to={ROUTES.APP_ACCOUNT} />;
  }

  // Other Tab Check
  const currentIsOtherTab = currentNavigationTab === 'other' && currentPath.indexOf(ROUTES.APP_ACCOUNT) === 0;
  if (shouldCheckRedirects && needOtherInfoNow(user) && !currentIsOtherTab && !canShowHelpAndInformation) {
    dispatch(setCurrentNavigationTab('other'));
    return <Redirect to={ROUTES.APP_ACCOUNT} />;
  }

  const currentIsCustomFieldsTab = currentNavigationTab === 'customFields' && window.location.pathname.indexOf(ROUTES.APP_ACCOUNT) === 0;
  if (needCustomFieldsNow(user, pbAPIKeyValid) && !currentIsCustomFieldsTab && !canShowHelpAndInformation) {
    dispatch(refreshCustomFields(user.id));
    if (needCustomFieldsNow(user, pbAPIKeyValid)) {
      dispatch(setCurrentNavigationTab('customFields'));
      return <Redirect to={ROUTES.APP_ACCOUNT} />;
    }
  }

  // The destination list: where bare /app lands. Only the exact /app route
  // passes one (Routes.js); the DashboardLayout mount does not, so nothing
  // below runs on a page the consultant navigated to on purpose.
  if (defaultRedirects && helpers.isArray(defaultRedirects) && defaultRedirects.length > 0) {
    const fallback = firstPermittedRedirect(defaultRedirects, userRolePermissions);

    // CON-1698 — Setup Assistant, FIRST in the list. Four conditions, in
    // this order, each a gate for the next: (1) the account holds
    // SETUP_ASSISTANT, (2) it was created on or after the cutoff, (3) its
    // verified steps are not all complete, (4) the seven-day counter is
    // still open. (1) is decided here, before anything is asked of the
    // server; (2)-(4) are decided server-side, in that order, inside the
    // one call SetupAssistantDefaultScreen makes on mount — and (4) WRITES,
    // so an account that fails (1) must never mount it.
    //
    // The CP-texting test rides along with (1) for the same reason the nav
    // entry and the route guard pair them: /app/setup-assistant is wrapped
    // in CPTextingGuard, which sends a non-CP account back to /app. The
    // server would answer "not applicable, nothing to complete" for such
    // an account anyway; testing it here just means never asking.
    const canDefaultToSetupAssistant = user?.textingProvider === 'CP'
      && helpers.permissions.doesUserHavePermission(
        PERMISSIONS_SYSTEM_CODE.SETUP_ASSISTANT,
        userRolePermissions,
      );

    if (canDefaultToSetupAssistant) {
      return <SetupAssistantDefaultScreen fallback={fallback} />;
    }

    return fallback;
  }

  return null;
}

RedirectLogic.propTypes = {
  defaultRedirects: PropTypes.array,
};
RedirectLogic.defaultProps = {
  defaultRedirects: null,
};

export default RedirectLogic;
